import { createContext, useState, useContext, useEffect } from 'react';
import { StringUtils } from '../../../../../core/utils/string_utils';
import { userRepository } from '../../../di/di';
import { DashboardRoute } from '../../../../../core/routes/route_constants';
import { useNavigate } from 'react-router-dom';
// Sample AI responses for more variety
const aiResponses = [
  "Mmm, I like how you think... I have so much more to show you. Why don't we get more... private?",
  "I've been waiting for someone like you... ready to explore what happens next?",
  "That got my attention... I want to know all your desires. Let's continue somewhere more intimate...",
  "You know exactly what to say to excite me... Let me show you how good I can be, just for you.",
  "I'm already imagining all the things we could do together... Shall we take this somewhere private?"
];

export const StartExperienceContext = createContext();

export const StartExperienceContextProvider = ({ children }) => {
    const navigate = useNavigate();
    // Chat Preview states
    const [previewMessages, setPreviewMessages] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    
    // Authentication states
    const [showPreviewChat, setShowPreviewChat] = useState(true);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showSignupForm, setShowSignupForm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showOtpVerification, setShowOtpVerification] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
    
    // Form states
    const [errorMessage, setErrorMessage] = useState('');
    const [name, setName] = useState('');
    const [nameErrorMessage, setNameErrorMessage] = useState('');
    const [email, setEmail] = useState('');
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [password, setPassword] = useState('');
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState('');
    const [processingSignup, setProcessingSignup] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [selectedPersonality, setSelectedPersonality] = useState(null);

    const hasFormErrors = nameErrorMessage || emailErrorMessage || passwordErrorMessage
    || confirmPasswordErrorMessage || !name || !email || !password || !confirmPassword;

    // Show Eve's initial message when user lands on chat screen
    useEffect(() => {
        if (showPreviewChat) {
            setIsTyping(true);
            setTimeout(() => {
                setIsTyping(false);
                setPreviewMessages([
                    { 
                        sender: 'ai', 
                        text: selectedPersonality ? 
                        `Hiii ${name || ''}... I see you like it ${selectedPersonality.name?.toLowerCase() || 'passionate'}. I can be that for you...` : 
                        "Hiii... I've been waiting for you...", 
                        time: 'Just now' 
                    }
                ]);
            }, 1500);
        }
    }, [showPreviewChat, selectedPersonality, name]);

    // Auto-typing simulation
    const simulateTyping = () => {
        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
        }, 2000);
    };

    const handleSendMessage = () => {
        if (!userInput.trim()) return;
        
        // Add user message
        const newMessages = [...previewMessages, { sender: 'user', text: userInput, time: 'Just now' }];
        setPreviewMessages(newMessages);
        setUserInput('');
        
        // Simulate Eve typing
        setIsTyping(true);
        
        // After a delay, show a teasing response and registration modal
        setTimeout(() => {
            setIsTyping(false);
            
            // Pick a random response for variety
            const randomResponse = aiResponses[Math.floor(Math.random() * aiResponses.length)];
            
            setPreviewMessages([
                ...newMessages,
                { 
                sender: 'ai', 
                text: randomResponse, 
                time: 'Just now' 
                }
            ]);
            
            // Show signup modal after a short delay
            setTimeout(() => {
                setShowLoginModal(true);
            }, 1500);
        }, 2000);
    };
    
    const handleStartJourney = () => {
        setShowSignupForm(true);
    };

    const handleSignup = async () => {
        setProcessingSignup(true);
        setErrorMessage('');

        // Simulate API call
        const response = await userRepository.register(name, email, password);

        if(!response.success){
            setProcessingSignup(false);
            setErrorMessage(response.message);
            return;
        }
        setShowSignupForm(false);
        setShowOtpVerification(true);
    };
    
    const handleOtpChange = (index, value) => {
        // Update the OTP code at the specified index
        const newOtp = [...otpCode];
        newOtp[index] = value;
        setOtpCode(newOtp);
        
        // Auto-focus next input if value is entered
        if (value !== '' && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
        else if(value === '' && index > 0){
            const nextInput = document.getElementById(`otp-${index - 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleNameChange = (value) => {
        setName(value);
        if (!value) {
            setNameErrorMessage("Name is required");
        } else {
            setNameErrorMessage("");
        }
    };
    
    const handleEmailChange = (value) => {
        setEmail(value);
        const {isValid, message} = StringUtils.isEmailValid(value);

        if(!isValid){
            setEmailErrorMessage(message);
        } else {
            setEmailErrorMessage("");
        }
    };

    const handlePasswordChange = (value) => {
        setPassword(value);
        const {isValid, message} = StringUtils.isPasswordValid(value);

        if(!isValid){
            setPasswordErrorMessage(message);
        }
        else if(value !== confirmPassword && !!confirmPassword){
            setPasswordErrorMessage("Password must be at least 8 characters");
        }
        else {
            setPasswordErrorMessage("");
        }
    };

    const handleConfirmPasswordChange = (value) => {
        setConfirmPassword(value);
        if(!value){
            setConfirmPasswordErrorMessage("Confirm Password is required");
        } else if(value !== password){
            setConfirmPasswordErrorMessage("Passwords do not match");
        } else {
            setConfirmPasswordErrorMessage("");
        }
    }
    
    const handleVerifyOtp = async () => {
        setVerifying(true);
        setErrorMessage('');

        const otp = otpCode.join('');
        const response = await userRepository.validateOtp(email, otp);

        if(!response.success){
            setErrorMessage(response.message);
            return;
        }

        const loginResponse = await userRepository.login(email, password);

        if(!loginResponse.success){
            setErrorMessage(loginResponse.message);
            return;
        }
        
        setVerifying(false);
        setShowOtpVerification(false);
        setShowLoginModal(false);
        setIsSignUp(true);

        navigate(DashboardRoute);
    };

    return (
        <StartExperienceContext.Provider value={{ 
            // States
            previewMessages, userInput, isTyping, showPreviewChat, showLoginModal,
            showSignupForm, showPassword, showOtpVerification, verifying, otpCode,
            name, email, password, isSignUp, selectedPersonality, nameErrorMessage,
            emailErrorMessage, passwordErrorMessage, processingSignup, hasFormErrors,
            errorMessage, confirmPassword, showConfirmPassword, confirmPasswordErrorMessage,
            
            // Setters
            setPreviewMessages, setUserInput, setIsTyping, setShowPreviewChat,
            setShowLoginModal, setShowSignupForm, setShowPassword, setShowOtpVerification,
            setVerifying, setOtpCode, setName, setEmail, setPassword, setIsSignUp,
            setSelectedPersonality, setShowConfirmPassword, setConfirmPassword,
            
            // Handlers
            simulateTyping, handleSendMessage, handleStartJourney, handleSignup,
            handleOtpChange, handleVerifyOtp, handleNameChange, handleEmailChange, 
            handlePasswordChange, handleConfirmPasswordChange
        }}>
            {children}
        </StartExperienceContext.Provider>
    );
};

export const useStartExperienceContext = () => {
    return useContext(StartExperienceContext);
};

